/**
 * Create or edit an item: identity, prices and VAT, then the packs it
 * comes in (CTN = 24 PCS), each with its own barcode and price.
 *
 * Saving is all-or-nothing on the server, so a carton with a duplicate
 * barcode never leaves the item half-saved. Every message the server
 * sends back names the field it is about, and is shown above the form.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, MenuItem, Stack, Switch, TextField,
  Typography, createFilterOptions, useMediaQuery,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { useTranslation } from 'react-i18next'

import { fetchInvItem, saveInvGroup, saveInvItem } from '../api/endpoints'
import { errorMessage } from '../api/client'
import type { InvGroup, InvItem, InvItemIn, InvStore, InvUnit, VatCategory } from '../api/types'
import { useUnsavedChanges } from '../store/UnsavedChanges'
import { PanelHeading } from '../components/ScreenTitle'
import { c, fmtMoney } from '../theme'
import { normalise, sanitize } from '../vouchers/money'
import { fmtQty, inPacks } from './qty'
import { gregorian } from '../reports/hijri'
import { P } from '../api/types'
import { useAuth } from '../store/AuthContext'

/** Units a Gulf trader actually uses, offered first; any other can be typed. */
export const COMMON_UNITS = ['PCS', 'CTN', 'BOX', 'PKT', 'PK6', 'PK12', 'DZN', 'BAG',
  'KG', 'GM', 'LTR', 'ML', 'MTR', 'ROLL', 'SET', 'PAIR', 'TIN', 'BTL', 'TRAY']

const blankUnit = (): InvUnit => ({ unit: '', factor: '', barcode: '', sale_price: '' })

type GroupOption = InvGroup | { inputValue: string; name_en: string; id?: undefined }
const filterGroups = createFilterOptions<GroupOption>()

export default function ItemDialog({ item, groups, stores, editable, onClose }: {
  item: InvItem | null
  groups: InvGroup[]
  stores: InvStore[]
  editable: boolean
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const qc = useQueryClient()
  const phone = useMediaQuery('(max-width:600px)')
  const ar = i18n.language === 'ar'
  const { can } = useAuth()
  // What was last paid, for whoever buys or sees costs anyway.
  const detail = useQuery({
    enabled: !!item && (can(P.postPurchase) || can(P.reportFinancial)),
    queryKey: ['inv-item', item?.id], queryFn: () => fetchInvItem(item!.id),
  })
  const last = detail.data?.last_purchase

  const initial: InvItemIn = useMemo(() => item ? {
    code: item.code, name_en: item.name_en, name_ar: item.name_ar ?? '',
    group_id: item.group_id ?? null, base_unit: item.base_unit,
    barcode: item.barcode ?? '', vat_category: item.vat_category,
    sale_price: item.sale_price ?? '', min_price: item.min_price ?? '',
    reorder_level: item.reorder_level ?? '',
    is_active: item.is_active,
    units: item.units.map((u) => ({ ...u, barcode: u.barcode ?? '',
                                     sale_price: u.sale_price ?? '' })),
  } : {
    code: '', name_en: '', name_ar: '', group_id: null, base_unit: 'PCS', barcode: '',
    vat_category: 'STANDARD', sale_price: '', min_price: '', reorder_level: '',
    is_active: true, units: [],
  }, [item])

  const [form, setForm] = useState<InvItemIn>(initial)
  const [error, setError] = useState<string | null>(null)
  const dirty = JSON.stringify(form) !== JSON.stringify(initial)
  useUnsavedChanges(dirty)

  const set = <K extends keyof InvItemIn>(k: K, v: InvItemIn[K]) => setForm((f) => ({ ...f, [k]: v }))
  const setUnit = (i: number, patch: Partial<InvUnit>) =>
    setForm((f) => ({ ...f, units: f.units.map((u, j) => (j === i ? { ...u, ...patch } : u)) }))

  const save = useMutation({
    mutationFn: async () => {
      let groupId = form.group_id
      if (pendingGroup) {
        groupId = (await saveInvGroup({ name_en: pendingGroup })).id
      }
      const clean = (v?: string | null) => (v ?? '').trim() || null
      return saveInvItem({
        ...form, group_id: groupId,
        code: form.code.trim().toUpperCase(), name_en: form.name_en.trim(),
        name_ar: clean(form.name_ar), barcode: clean(form.barcode),
        sale_price: clean(form.sale_price), min_price: clean(form.min_price),
        reorder_level: clean(form.reorder_level),
        units: form.units.filter((u) => u.unit.trim()).map((u) => ({
          unit: u.unit.trim().toUpperCase(), factor: u.factor.trim(),
          barcode: clean(u.barcode), sale_price: clean(u.sale_price) })),
      }, item?.id)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inv-items'] })
      void qc.invalidateQueries({ queryKey: ['inv-groups'] })
      setForm(initial)          // clean, so closing does not ask
      onClose()
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const [pendingGroup, setPendingGroup] = useState<string | null>(null)
  const groupValue: GroupOption | null = pendingGroup
    ? { inputValue: pendingGroup, name_en: pendingGroup }
    : groups.find((g) => g.id === form.group_id) ?? null

  const money = (v: string) => sanitize(v)
  const ro = !editable

  return (
    <Dialog open onClose={() => !dirty && onClose()} maxWidth="md" fullWidth fullScreen={phone}
      aria-labelledby="item-dialog-title">
      <DialogTitle id="item-dialog-title" sx={{ pb: 0.5 }}>
        {item ? (ro ? item.name_en : t('inv.editItem', { code: item.code })) : t('inv.newItem')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('inv.code')} value={form.code} required disabled={ro}
              autoFocus={!item} sx={{ width: { sm: 180 } }}
              inputProps={{ maxLength: 30, style: { textTransform: 'uppercase' } }}
              onChange={(e) => set('code', e.target.value)} />
            <Autocomplete<GroupOption, false, false, true>
              freeSolo selectOnFocus clearOnBlur handleHomeEndKeys disabled={ro}
              options={groups} value={groupValue} sx={{ flex: 1 }}
              getOptionLabel={(o) => typeof o === 'string' ? o
                : 'inputValue' in o ? o.inputValue : ((ar && (o as InvGroup).name_ar) || o.name_en)}
              filterOptions={(opts, params) => {
                const found = filterGroups(opts, params)
                const typed = params.inputValue.trim()
                if (typed && !opts.some((o) => o.name_en.toLowerCase() === typed.toLowerCase())) {
                  found.push({ inputValue: typed, name_en: t('inv.addGroup', { name: typed }) })
                }
                return found
              }}
              renderOption={(props, o) => <li {...props} key={'id' in o && o.id ? o.id : o.name_en}>
                {'id' in o && o.id ? ((ar && (o as InvGroup).name_ar) || o.name_en) : o.name_en}</li>}
              onChange={(_, v) => {
                if (v === null) { setPendingGroup(null); set('group_id', null) }
                else if (typeof v === 'string') { setPendingGroup(v.trim() || null); set('group_id', null) }
                else if ('inputValue' in v) { setPendingGroup(v.inputValue); set('group_id', null) }
                else { setPendingGroup(null); set('group_id', v.id) }
              }}
              renderInput={(p) => <TextField {...p} label={t('inv.group')} />} />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('inv.nameEn')} value={form.name_en} required disabled={ro}
              sx={{ flex: 1 }} onChange={(e) => set('name_en', e.target.value)} />
            <TextField label={t('inv.nameAr')} value={form.name_ar ?? ''} disabled={ro}
              sx={{ flex: 1 }} inputProps={{ dir: 'rtl' }}
              onChange={(e) => set('name_ar', e.target.value)} />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Autocomplete freeSolo options={COMMON_UNITS} value={form.base_unit} disabled={ro}
              sx={{ width: { sm: 150 } }}
              onInputChange={(_, v) => set('base_unit', v.toUpperCase())}
              renderInput={(p) => <TextField {...p} label={t('inv.baseUnit')}
                helperText={t('inv.baseUnitHint')} />} />
            <TextField label={t('inv.barcode')} value={form.barcode ?? ''} disabled={ro}
              sx={{ flex: 1 }} inputProps={{ inputMode: 'numeric', style: { textAlign: 'start' } }}
              helperText={t('inv.barcodeHint', { unit: form.base_unit || 'PCS' })}
              onChange={(e) => set('barcode', e.target.value.trim())} />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select label={t('inv.vat')} value={form.vat_category} disabled={ro}
              sx={{ width: { sm: 190 } }}
              onChange={(e) => set('vat_category', e.target.value as VatCategory)}>
              <MenuItem value="STANDARD">{t('inv.vatSTANDARD')} · 15%</MenuItem>
              <MenuItem value="ZERO">{t('inv.vatZERO')} · 0%</MenuItem>
              <MenuItem value="EXEMPT">{t('inv.vatEXEMPT')}</MenuItem>
            </TextField>
            <TextField label={t('inv.salePrice', { unit: form.base_unit || 'PCS' })}
              value={form.sale_price ?? ''} disabled={ro} sx={{ flex: 1 }}
              inputProps={{ inputMode: 'decimal', className: 'num' }}
              helperText={t('inv.priceExVat')}
              onChange={(e) => set('sale_price', money(e.target.value))}
              onBlur={(e) => e.target.value && set('sale_price', normalise(e.target.value))} />
            <TextField label={t('inv.minPrice')} value={form.min_price ?? ''} disabled={ro}
              sx={{ flex: 1 }} inputProps={{ inputMode: 'decimal', className: 'num' }}
              helperText={t('inv.minPriceHint')}
              onChange={(e) => set('min_price', money(e.target.value))}
              onBlur={(e) => e.target.value && set('min_price', normalise(e.target.value))} />
            <TextField label={t('stock.reorderLevelIn', { unit: form.base_unit || 'PCS' })}
              value={form.reorder_level ?? ''} disabled={ro} sx={{ flex: 1 }}
              inputProps={{ inputMode: 'decimal', className: 'num' }}
              helperText={t('stock.reorderHint')}
              onChange={(e) => set('reorder_level', e.target.value.replace(/[^0-9.]/g, ''))} />
          </Stack>

          <Box>
            <PanelHeading hint={t('inv.unitsHint', { unit: form.base_unit || 'PCS' })}>
              {t('inv.packs')}</PanelHeading>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {form.units.map((u, i) => (
                <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1}
                  alignItems={{ sm: 'center' }}
                  sx={{ p: { xs: 1.25, sm: 0 }, border: { xs: `1px solid ${c.border}`, sm: 0 },
                        borderRadius: '8px' }}>
                  <Autocomplete freeSolo options={COMMON_UNITS.filter((x) => x !== form.base_unit)}
                    value={u.unit} disabled={ro} sx={{ width: { sm: 130 } }}
                    onInputChange={(_, v) => setUnit(i, { unit: v.toUpperCase() })}
                    renderInput={(p) => <TextField {...p} size="small" label={t('inv.unit')} />} />
                  <TextField size="small" label={t('inv.factor', { unit: form.base_unit || 'PCS' })}
                    value={u.factor} disabled={ro} sx={{ width: { sm: 150 } }}
                    inputProps={{ inputMode: 'decimal', className: 'num' }}
                    onChange={(e) => setUnit(i, { factor: sanitize(e.target.value) })} />
                  <TextField size="small" label={t('inv.barcode')} value={u.barcode ?? ''}
                    disabled={ro} sx={{ flex: 1 }} inputProps={{ inputMode: 'numeric', style: { textAlign: 'start' } }}
                    onChange={(e) => setUnit(i, { barcode: e.target.value.trim() })} />
                  <TextField size="small" label={t('inv.packPrice')} value={u.sale_price ?? ''}
                    disabled={ro} sx={{ width: { sm: 140 } }}
                    placeholder={autoPrice(form.sale_price, u.factor)}
                    inputProps={{ inputMode: 'decimal', className: 'num' }}
                    onChange={(e) => setUnit(i, { sale_price: sanitize(e.target.value) })}
                    onBlur={(e) => e.target.value && setUnit(i, { sale_price: normalise(e.target.value) })} />
                  {!ro && (
                    <IconButton aria-label={t('inv.removeUnit', { unit: u.unit || '' })}
                      onClick={() => set('units', form.units.filter((_, j) => j !== i))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
              {!ro && (
                <Box><Button size="small" startIcon={<AddIcon />}
                  onClick={() => set('units', [...form.units, blankUnit()])}>
                  {t('inv.addPack')}</Button></Box>
              )}
            </Stack>
          </Box>

          {item && (
            <Box>
              <PanelHeading>{t('inv.stockByStore')}</PanelHeading>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {stores.map((s) => {
                  const q = item.stock[String(s.id)] ?? '0'
                  return (
                    <Stack key={s.id} direction="row" justifyContent="space-between"
                      sx={{ fontSize: 14, py: 0.5, borderBottom: `1px solid ${c.divider}` }}>
                      <span>{(ar && s.name_ar) || s.name_en}</span>
                      <span className="num" style={{ color: Number(q) < 0 ? 'var(--danger-ink)' : undefined }}>
                        {Number(q) > 0 ? inPacks(q, item) : `${fmtQty(q)} ${item.base_unit}`}
                      </span>
                    </Stack>
                  )
                })}
                {last && (
                  <Typography sx={{ fontSize: 12.5, color: c.inkMuted, pt: 0.5 }}>
                    {t('inv.lastBought', { cost: last.unit_cost, unit: last.unit,
                                           supplier: last.supplier_name,
                                           date: gregorian(last.date, i18n.language),
                                           doc: last.doc_no })}
                  </Typography>
                )}
                {item.avg_cost !== undefined && (
                  <Typography sx={{ fontSize: 12.5, color: c.inkMuted, pt: 0.5 }}>
                    {t('inv.avgCostLine', { cost: Number(item.avg_cost).toFixed(4),
                                             unit: item.base_unit,
                                             value: fmtMoney(item.stock_value ?? '0') })}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          <FormControlLabel disabled={ro} control={<Switch checked={form.is_active}
            onChange={(_, v) => set('is_active', v)} />} label={t('inv.activeHint')} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>{ro ? t('common.close') : t('common.cancel')}</Button>
        {!ro && (
          <Button variant="contained" disabled={save.isPending || !form.code.trim()
            || !form.name_en.trim() || !dirty} onClick={() => save.mutate()}>
            {t('common.save')}</Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

/** The pack price a blank field will mean: factor x the base price. */
function autoPrice(base?: string | null, factor?: string): string {
  const b = Number(base), f = Number(factor)
  return b > 0 && f > 1 ? (b * f).toFixed(2) : ''
}
